import { useEffect, useState } from 'react';

import { now as serverNow } from '../../offline';

/**
 * "Now", on the **server's** clock, re-read once a second and **paused while
 * the tab is hidden**.
 *
 * Two things this is not:
 *
 * - It is not `Date.now()`. Elapsed time on this screen comes from the
 *   server-confirmed `openedAt` plus the persisted clock offset (D-10); a
 *   phone whose clock is forty seconds slow must not show forty seconds of
 *   work nobody will be paid for.
 * - It is not an animation. `DESIGN.md` forbids animating a payroll fact, so
 *   the readout re-renders on a whole second and never transitions.
 *
 * The pause matters on a phone that spends the night in a pocket: an interval
 * that keeps firing in a hidden tab is battery a night worker cannot spare,
 * and the first tick after `visibilitychange` re-reads the real clock anyway,
 * so nothing drifts.
 *
 * `active` is the same flag admin's `useTick(ms, active)` carries, for the
 * same reason: a visible screen where nothing counts — no elapsed time, no
 * countdown — must not reconcile the punch screen once a second while a gloved
 * thumb is aiming at the key. Turning it back on re-reads the clock
 * immediately, so the value is never served stale.
 */
export function useServerNow(intervalMs = 1_000, active = true): number {
  const [value, setValue] = useState<number>(() => serverNow());

  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = (): void => {
      setValue(serverNow());
    };

    const stop = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = (): void => {
      stop();
      tick();
      timer = setInterval(tick, intervalMs);
    };

    const hidden = (): boolean =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const onVisibilityChange = (): void => {
      if (hidden()) stop();
      else start();
    };

    if (!hidden()) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, active]);

  return value;
}
